import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Shield, ShieldCheck, ShieldAlert, Loader2, Check, Copy, AlertTriangle, KeyRound,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin 2FA settings tab.
 *
 * Sections:
 *  1. Status banner (enabled / disabled / org-enforced)
 *  2. Setup wizard: /setup -> QR + secret -> /confirm with first code -> backup codes shown ONCE
 *  3. Disable + regenerate backup codes (require current TOTP)
 *  4. Super-admin only: organization enforcement toggle
 */
const AdminTwoFactorTab = () => {
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin' || user?.role === 'admin';

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Setup flow
  const [setupData, setSetupData] = useState(null); // { secret, otpauth_uri, qr_image }
  const [setupCode, setSetupCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState('');
  const [backupCodes, setBackupCodes] = useState(null); // shown once after confirm
  const [copied, setCopied] = useState(false);

  // Disable flow
  const [disableCode, setDisableCode] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);
  const [disableError, setDisableError] = useState('');

  // Regenerate backups
  const [regenCode, setRegenCode] = useState('');
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenError, setRegenError] = useState('');

  // Policy
  const [policy, setPolicy] = useState(null);
  const [policyLoading, setPolicyLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/2fa/status`, { withCredentials: true });
      setStatus(r.data);
      if (isSuper) {
        const p = await axios.get(`${BACKEND_URL}/api/admin/2fa/policy`, { withCredentials: true });
        setPolicy(p.data);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load 2FA status');
    } finally {
      setLoading(false);
    }
  }, [isSuper]);

  useEffect(() => { refresh(); }, [refresh]);

  const startSetup = async () => {
    setSetupError(''); setSetupLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/2fa/setup`, {}, { withCredentials: true });
      setSetupData(r.data);
      setBackupCodes(null);
      setSetupCode('');
    } catch (e) {
      setSetupError(e.response?.data?.detail || 'Setup failed');
    } finally {
      setSetupLoading(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setSetupError(''); setSetupLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/2fa/confirm`,
        { code: setupCode.trim() }, { withCredentials: true });
      setBackupCodes(r.data.backup_codes);
      setSetupCode('');
      setSetupData(null);
      await refresh();
    } catch (e2) {
      setSetupError(e2.response?.data?.detail || 'Invalid code');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setDisableError(''); setDisableLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/2fa/disable`,
        { code: disableCode.trim() }, { withCredentials: true });
      setDisableCode('');
      await refresh();
    } catch (e2) {
      setDisableError(e2.response?.data?.detail || 'Disable failed');
    } finally {
      setDisableLoading(false);
    }
  };

  const handleRegen = async (e) => {
    e.preventDefault();
    setRegenError(''); setRegenLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/2fa/regenerate-backups`,
        { code: regenCode.trim() }, { withCredentials: true });
      setBackupCodes(r.data.backup_codes);
      setRegenCode('');
      await refresh();
    } catch (e2) {
      setRegenError(e2.response?.data?.detail || 'Could not regenerate');
    } finally {
      setRegenLoading(false);
    }
  };

  const togglePolicy = async () => {
    if (!policy) return;
    const next = !policy.enforced;
    const msg = next
      ? 'Enforce 2FA organization-wide? All admins without 2FA will be required to set it up on their next sign-in.'
      : 'Disable organization 2FA enforcement? Individual 2FA settings remain in place.';
    if (!window.confirm(msg)) return;
    setPolicyLoading(true);
    try {
      const r = await axios.put(`${BACKEND_URL}/api/admin/2fa/policy`,
        { enforced: next }, { withCredentials: true });
      setPolicy(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update policy');
    } finally {
      setPolicyLoading(false);
    }
  };

  const copyAll = () => {
    if (!backupCodes) return;
    navigator.clipboard.writeText(backupCodes.join('\n')).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2500);
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>;
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-2fa-tab">
      <div>
        <h2 className="font-heading text-lg font-bold text-[#0A0A0A] flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#BA1826]" />
          Two-Factor Authentication
        </h2>
        <p className="text-sm text-[#4B5563] mt-1">
          Add a second sign-in step using an authenticator app (Google Authenticator, Authy, 1Password).
          Backup codes and an email-based recovery option are available if you lose your device.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* STATUS BANNER */}
      <div className={`border rounded-lg p-4 flex items-start gap-3 ${
        status?.enabled ? 'bg-green-50 border-green-200' :
        status?.policy_enforced ? 'bg-amber-50 border-amber-200' :
        'bg-[#F8F9FA] border-[#E5E7EB]'
      }`} data-testid="two-factor-status-banner">
        {status?.enabled ? <ShieldCheck className="w-6 h-6 text-green-700 flex-shrink-0" /> :
          status?.policy_enforced ? <ShieldAlert className="w-6 h-6 text-amber-700 flex-shrink-0" /> :
          <Shield className="w-6 h-6 text-[#9CA3AF] flex-shrink-0" />}
        <div className="flex-1">
          <p className="font-semibold text-sm">
            {status?.enabled ? '2FA is enabled on your account' :
              status?.policy_enforced ? '2FA is REQUIRED by your organization' :
              '2FA is not enabled'}
          </p>
          {status?.enabled && (
            <p className="text-xs text-[#4B5563] mt-0.5">
              Enrolled {status.enrolled_at ? new Date(status.enrolled_at).toLocaleDateString() : ''} ·
              {' '}{status.backup_codes_remaining || 0} backup codes remaining
            </p>
          )}
          {!status?.enabled && status?.policy_enforced && (
            <p className="text-xs text-amber-800 mt-0.5">Your super admin has enforced 2FA. Please set it up below.</p>
          )}
        </div>
      </div>

      {/* SETUP WIZARD */}
      {!status?.enabled && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 space-y-4" data-testid="two-factor-setup-section">
          <h3 className="font-heading text-base font-bold flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#BA1826]" /> Enable Two-Factor Authentication
          </h3>
          {!setupData ? (
            <>
              <p className="text-sm text-[#4B5563]">
                You'll scan a QR code with your authenticator app, then enter the 6-digit code it generates to confirm.
              </p>
              <button onClick={startSetup} disabled={setupLoading}
                className="btn-primary text-sm disabled:opacity-50" data-testid="two-factor-start-setup-btn">
                {setupLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting...</> : <>Start Setup</>}
              </button>
            </>
          ) : (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-[200px_1fr] gap-4 items-start">
                <img src={setupData.qr_image} alt="2FA QR" className="w-[200px] h-[200px] border border-[#E5E7EB] rounded bg-white" data-testid="two-factor-qr-image" />
                <div className="space-y-2">
                  <p className="text-sm text-[#4B5563]">
                    <strong>Step 1.</strong> Scan this QR with Google Authenticator, Authy, or 1Password.
                  </p>
                  <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded p-2">
                    <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Can't scan? Enter this secret manually:</p>
                    <code className="text-xs font-mono break-all" data-testid="two-factor-secret">{setupData.secret}</code>
                  </div>
                  <p className="text-sm text-[#4B5563] pt-2">
                    <strong>Step 2.</strong> Enter the 6-digit code from your app to confirm.
                  </p>
                </div>
              </div>
              <form onSubmit={confirmSetup} className="flex items-center gap-2">
                <input type="text" value={setupCode} onChange={(e) => setSetupCode(e.target.value)}
                  placeholder="123 456" maxLength={10}
                  className="flex-1 max-w-[220px] px-3 py-2 border border-[#E5E7EB] rounded-sm text-base font-mono tracking-widest text-center"
                  autoFocus data-testid="two-factor-confirm-code-input" />
                <button type="submit" disabled={setupLoading || !setupCode.trim()}
                  className="btn-primary text-sm disabled:opacity-50"
                  data-testid="two-factor-confirm-btn">
                  {setupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Enable'}
                </button>
                <button type="button" onClick={() => setSetupData(null)}
                  className="text-xs text-[#4B5563] hover:underline" data-testid="two-factor-cancel-setup-btn">
                  Cancel
                </button>
              </form>
              {setupError && (
                <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {setupError}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* BACKUP CODES — shown ONCE */}
      {backupCodes && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-5 space-y-3" data-testid="two-factor-backup-codes-panel">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
            <h3 className="font-heading font-bold text-amber-900">Save your backup codes now</h3>
          </div>
          <p className="text-sm text-amber-900">
            These one-time codes let you sign in if you lose your authenticator app.
            <strong> Each code works once.</strong> Store them somewhere safe — they will not be shown again.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono text-sm" data-testid="two-factor-backup-codes-list">
            {backupCodes.map((c) => (
              <div key={c} className="bg-white border border-amber-200 rounded px-3 py-2 text-center">{c}</div>
            ))}
          </div>
          <button onClick={copyAll} className="text-xs font-semibold inline-flex items-center gap-1 bg-amber-900 text-white px-3 py-1.5 rounded hover:bg-amber-800"
            data-testid="two-factor-copy-backup-codes-btn">
            {copied ? <><Check className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy all codes</>}
          </button>
        </div>
      )}

      {/* MANAGE: regen + disable */}
      {status?.enabled && (
        <div className="space-y-4">
          {/* Regenerate */}
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 space-y-3" data-testid="two-factor-regen-section">
            <h3 className="font-heading text-base font-bold">Regenerate backup codes</h3>
            <p className="text-sm text-[#4B5563]">
              Generates a fresh set of 10 one-time codes. Old codes stop working immediately. Requires a current authenticator code.
            </p>
            <form onSubmit={handleRegen} className="flex items-center gap-2">
              <input type="text" value={regenCode} onChange={(e) => setRegenCode(e.target.value)}
                placeholder="6-digit code" maxLength={10}
                className="max-w-[180px] px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm font-mono tracking-widest text-center"
                data-testid="two-factor-regen-code-input" />
              <button type="submit" disabled={regenLoading || !regenCode.trim()}
                className="text-sm bg-[#0A0A0A] text-white px-4 py-2 rounded hover:bg-[#333] disabled:opacity-50"
                data-testid="two-factor-regen-btn">
                {regenLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Regenerate'}
              </button>
            </form>
            {regenError && <p className="text-xs text-red-600">{regenError}</p>}
          </div>

          {/* Disable */}
          <div className="bg-white border border-red-200 rounded-lg p-5 space-y-3" data-testid="two-factor-disable-section">
            <h3 className="font-heading text-base font-bold text-red-700">Disable Two-Factor Authentication</h3>
            <p className="text-sm text-[#4B5563]">
              Disabling 2FA makes your admin account easier to compromise. You'll get a confirmation email if you proceed.
            </p>
            {status?.policy_enforced && user?.role !== 'super_admin' && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                Your organization requires 2FA — ask a super admin to relax the policy before disabling.
              </p>
            )}
            <form onSubmit={handleDisable} className="flex items-center gap-2">
              <input type="text" value={disableCode} onChange={(e) => setDisableCode(e.target.value)}
                placeholder="6-digit or backup code" maxLength={20}
                className="flex-1 max-w-[260px] px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm font-mono tracking-widest text-center"
                data-testid="two-factor-disable-code-input" />
              <button type="submit" disabled={disableLoading || !disableCode.trim()}
                className="text-sm bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                data-testid="two-factor-disable-btn">
                {disableLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disable 2FA'}
              </button>
            </form>
            {disableError && <p className="text-xs text-red-600">{disableError}</p>}
          </div>
        </div>
      )}

      {/* ORG POLICY (super admin only) */}
      {isSuper && policy && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 space-y-3" data-testid="two-factor-policy-section">
          <h3 className="font-heading text-base font-bold flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#BA1826]" /> Organization-wide enforcement
          </h3>
          <p className="text-sm text-[#4B5563]">
            When enforced, every admin must enable 2FA. Admins who haven't yet enrolled will see a setup prompt on their next sign-in.
          </p>
          <div className="flex items-center gap-4">
            <button onClick={togglePolicy} disabled={policyLoading}
              className={`w-12 h-6 rounded-full transition-colors relative ${policy.enforced ? 'bg-[#BA1826]' : 'bg-gray-300'} disabled:opacity-50`}
              data-testid="two-factor-policy-toggle">
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${policy.enforced ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
            <span className="text-sm font-medium">{policy.enforced ? 'Enforced' : 'Optional (per admin)'}</span>
            {policyLoading && <Loader2 className="w-4 h-4 animate-spin text-[#9CA3AF]" />}
          </div>
          {policy.updated_at && (
            <p className="text-[11px] text-[#9CA3AF]">
              Last updated {new Date(policy.updated_at).toLocaleString()} by {policy.updated_by}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminTwoFactorTab;
